export type AdminTournamentPathOptions = {
  tab?: string;
  suffix?: string;
  legacy?: boolean;
};

/** Canonical public tournament dashboard path. */
export function buildPublicTournamentPath(tournamentId: string): string {
  return `/tournament/${tournamentId}`;
}

/** Short public link path — resolves via GET /t/:code → /tournament/{id}. */
export function buildShortPublicPath(shortCode: string): string {
  return `/t/${shortCode}`;
}

/** Org-scoped admin tournament command center path. */
export function buildAdminTournamentPath(
  orgId: string,
  tournamentId: string,
  options?: AdminTournamentPathOptions
): string {
  const segment = options?.legacy ? 'tournaments/tournament' : 'tournaments';
  let path = `/org/${orgId}/${segment}/${tournamentId}`;
  if (options?.suffix) {
    path += options.suffix.startsWith('/') ? options.suffix : `/${options.suffix}`;
  }
  if (options?.tab) {
    path += `?tab=${encodeURIComponent(options.tab)}`;
  }
  return path;
}

/** Legacy admin path kept for bookmarks and dual-route compatibility. */
export function buildLegacyAdminTournamentPath(
  orgId: string,
  tournamentId: string,
  options?: Omit<AdminTournamentPathOptions, 'legacy'>
): string {
  return buildAdminTournamentPath(orgId, tournamentId, { ...options, legacy: true });
}

const ADMIN_TOURNAMENT_RESERVED_SEGMENTS = new Set(['create']);

/**
 * Extract tournament ID from admin URL pathnames.
 * Supports canonical, legacy org-scoped, and legacy shortcut routes.
 */
export function parseAdminTournamentIdFromPath(pathname: string): string | null {
  const path = pathname.split('?')[0]?.split('#')[0] ?? pathname;

  const legacyOrg = path.match(/^\/org\/[^/]+\/tournaments\/tournament\/([^/?]+)/);
  if (legacyOrg) {
    return legacyOrg[1];
  }

  const canonicalOrg = path.match(/^\/org\/[^/]+\/tournaments\/([^/?]+)/);
  if (canonicalOrg && !ADMIN_TOURNAMENT_RESERVED_SEGMENTS.has(canonicalOrg[1])) {
    return canonicalOrg[1];
  }

  const adminPortal = path.match(/^\/admin-portal\/([^/?]+)/);
  if (adminPortal) {
    return adminPortal[1];
  }

  const adminShortcut = path.match(
    /^\/admin\/(?!league(?:\/|$)|org(?:\/|$)|tournaments(?:\/|$)|booking(?:\/|$))([^/?]+)/
  );
  if (adminShortcut) {
    return adminShortcut[1];
  }

  const adminOrg = path.match(/^\/admin\/org\/[^/]+\/tournament\/([^/?]+)/);
  if (adminOrg) {
    return adminOrg[1];
  }

  return null;
}

export function buildAbsoluteUrl(origin: string, path: string): string {
  const base = origin.replace(/\/$/, '');
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
}

/** Prefer short public URL when code exists; otherwise canonical long path. */
export function buildPreferredPublicPath(tournament: {
  id: string;
  shortCode?: string | null;
}): string {
  if (tournament.shortCode) {
    return buildShortPublicPath(tournament.shortCode);
  }
  return buildPublicTournamentPath(tournament.id);
}
